/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { RootState } from 'store';

import { hotelsSearchParameters } from 'action-creators/hotels';
import { confirmHotelSolicitation, getHotelDetails, hotelResults, validateHotel } from 'actions/hotels';
import { fetchUserAccount } from 'actions/user';
import useFetching from 'hooks/useFetching';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { ConfirmMessage } from 'models/confirmMessage';
import { HotelDetailsCompleteResponse, RoomRatePrice } from 'models/hotelDetailsComplete';
import { HotelFilter } from 'models/hotelFilter';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import { HotelValidateResponse } from 'models/hotelValidate';
import HotelDetails from 'screens/components/HotelDetails';
import { amplitudelogEvent, ProceedToCheckoutEvent, ViewTravelItemDetailEvent } from 'services/amplitudeService';
import { HotelAmenities } from 'services/hotelService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage, getCurrentLanguage } from 'utils/generic';
import { City } from 'models/city';

const INCOMPLETE_PROFILE_ERROR_MESSAGES = ["'Gender'", "'Phone'", "'DOB'"];

interface Props {
  show: boolean;
  optionId?: number;
  onClose: () => void;
  amenitiesCodes: HotelAmenities[];
  travelers?: TravelArrangerUsersAllowed[];
  index?: number;
  destinationPoint: TravelHotelContentLocationRequest | City | undefined;
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
  onFilterChange: (filters: HotelFilter) => void;
  isFilterLoading: (isLoading: boolean) => void;
}

const HotelDetailsContainer = ({
  show,
  optionId,
  onClose,
  amenitiesCodes,
  travelers,
  index,
  destinationPoint,
  checkInDate,
  checkOutDate,
  onFilterChange,
  isFilterLoading,
}: Props) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [data, setData] = useState<HotelDetailsCompleteResponse | undefined>();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmFlow, setShowConfirmFlow] = useState(false);
  const [confirmFlowMessage, setConfirmFlowMessage] = useState<ConfirmMessage>({});
  const [confirmFlowType, setConfirmFlowType] = useState<string>();
  const [showTravelJustify, setShowTravelJustify] = useState(false);
  const [showConfirmProfile, setShowConfirmProfile] = useState(false);

  const { searchId, accountResult, distanceRestriction, filters, fullState } = useSelector((state: RootState) => ({
    accountResult: state.user.accountResult,
    searchId: state.hotels.search?.searchId,
    distanceRestriction: state.hotels.search?.distanceRestriction,
    filters: state.hotels.filters,
    fullState: state,
  }));

  const fetchHotelDetails = async () => {
    if (!accountResult || !searchId || !optionId || !distanceRestriction) return;

    try {
      setIsLoading(true);
      const {
        data: { data: details },
      } = await getHotelDetails({
        sessionToken: accountResult.sessionToken,
        searchId,
        optionId,
        culture: getCurrentLanguage(),
        distanceRestriction,
        userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId : accountResult.id,
        travelArrangerUserId: travelers && travelers.length > 0 && travelers[0].id !== accountResult.id ? accountResult.id : undefined,
      });
      setData({ ...details, optionId: `${optionId}` });

      const amplitudeEvent: ViewTravelItemDetailEvent = {
        INDEX: index ?? 0,
        SOURCE: 'LIST',
        TRAVEL_PRODUCT_TYPE: 'HOTELS',
        TRAVEL_FROM: '',
        TRAVEL_TO: details?.cityCode ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), details?.checkinDate).toString(),
        TRAVEL_DATE_FROM: details?.checkinDate?.toString() ?? '',
        TRAVEL_DATE_TO: details?.checkoutDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: daysCount(details?.checkinDate, details?.checkoutDate).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: details?.searchId ?? '',
        TRAVEL_PRODUCT_CODE: details?.hotelCode ?? '',
        TRAVEL_PRODUCT_NAME: details?.hotelName ?? '',
        TRAVEL_SUB_PRODUCT: '',
        TRAVEL_PRODUCT_CATEGORY: details?.category?.toString() ?? '',
        TRAVEL_PRODUCT_PRICE: details
          ? (
              details?.rooms.first().roomRate?.price?.total / daysCount(details?.checkinDate, details?.checkoutDate)
            ).toString()
          : '0',
        TRAVEL_PRODUCT_PROVIDER: details?.hotelProvider ?? '',
        TRAVEL_FARE_TYPE: '',
      };

      amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_ITEM_DETAIL, amplitudeEvent, accountResult);

      setIsLoading(false);
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'hotel',
        message: 'Hotel details solicitation failed',
        data: { error: extractErrorMessage(ex) },
      });
      setData(undefined);
      setIsLoading(false);

      Sentry.captureMessage('[HotelDetails] Could not retrieve hotel details');
    }
  };

  useFetching(() => {
    if (optionId && show) fetchHotelDetails();
  }, [optionId, show]);

  const handleConfirm = async (
    justifyForBreakingRules: string,
    reason: string,
    roomRateId: string,
    roomRate: { refundableType: string },
    roomRatePrice: RoomRatePrice,
    costCenterId?: string,
    breakRulesMessageId?: string,
    card?: string
  ) => {
    if (!data) return;

    setIsCreating(true);

    const amplitudeEvent: ProceedToCheckoutEvent = {
      SOURCE: 'BOOKING',
      TRAVEL_PRODUCT_TYPE: 'HOTELS',
      TRAVEL_FROM: '',
      TRAVEL_TO: data?.cityCode ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), data?.checkinDate).toString(),
      TRAVEL_DATE_FROM: data?.checkinDate?.toString() ?? '',
      TRAVEL_DATE_TO: data?.checkoutDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(data?.checkinDate, data?.checkoutDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: data?.searchId ?? '',
      TRAVEL_PRODUCT_CODE: data?.hotelCode ?? '',
      TRAVEL_PRODUCT_NAME: data?.hotelName ?? '',
      TRAVEL_SUB_PRODUCT: '',
      TRAVEL_PRODUCT_CATEGORY: data?.category?.toString() ?? '',
      TRAVEL_PRODUCT_PRICE: data
        ? (data?.rooms.first().roomRate?.price?.total / daysCount(data?.checkinDate, data?.checkoutDate)).toString()
        : '0',
      TRAVEL_PRODUCT_PROVIDER: data?.hotelProvider ?? '',
      TRAVEL_FARE_TYPE: '',
    };

    amplitudelogEvent(amplitudeEvents.PROCEED_TO_CHECKOUT, amplitudeEvent, accountResult);

    try {
      await dispatch<any>(fetchUserAccount());

      const responseValidate = await validateHotel(
        data,
        justifyForBreakingRules,
        reason,
        roomRateId,
        roomRate,
        roomRatePrice,
        fullState,
        costCenterId,
        card
      );

      if (!responseValidate) {
        Sentry.addBreadcrumb({
          category: 'hotel',
          message: 'Confirm hotel solicitation error',
          data: { error: 'responseValidate is null or undefined' },
        });

        throw false;
      }

      if (!responseValidate.succeeded) {
        Sentry.addBreadcrumb({
          category: 'hotel',
          message: 'Confirm hotel solicitation error',
          data: { error: 'responseValidate.succeeded is false', response: responseValidate },
        });
      }

      const hotelValidate: HotelValidateResponse = {
        documentId: responseValidate.data.documentId,
        validationId: responseValidate.data.validationId,
        validateResultType: '',
      };

      await dispatch<any>(
        confirmHotelSolicitation(
          data,
          justifyForBreakingRules,
          reason,
          roomRateId,
          roomRate,
          roomRatePrice,
          hotelValidate,
          costCenterId,
          breakRulesMessageId,
          card
        ),
      );
      setShowSuccess(true);
    } catch (ex) {
      const error = extractErrorMessage(ex);

      setIsCreating(false);

      let message = t('hotelDetails.error.generic');
      if (INCOMPLETE_PROFILE_ERROR_MESSAGES.some((msg) => error?.startsWith(msg))) {
        message = t('hotelDetails.error.incompleteProfile');
      }

      Sentry.addBreadcrumb({
        category: 'hotel',
        message: 'Confirm hotel solicitation error',
        data: { error, message },
      });

      if (error?.indexOf('Error to validate selected hotel') !== -1) {
        setConfirmFlowType('invalidSearchId');
        setConfirmFlow({
          text: t('hotelDetails.error.invalidOptionId.text'),
          button: t('hotelDetails.error.invalidOptionId.button'),
          title: t('hotelDetails.error.invalidOptionId.title'),
        });
        return;
      }

      if (error?.indexOf('Hotel Option is not available') !== -1) {
        setConfirmFlowType('invalidSearchId');
        setConfirmFlow({
          text: t('hotelDetails.error.invalidOptionId.text'),
          button: t('hotelDetails.error.invalidOptionId.button'),
          title: t('hotelDetails.error.invalidOptionId.title'),
        });
        return;
      }

      if (error?.indexOf('Invalid SearchId') !== -1) {
        setConfirmFlowType('invalidSearchId');
        setConfirmFlow({
          text: t('hotelDetails.error.invalidSearchId.text'),
          button: t('hotelDetails.error.invalidSearchId.button'),
          title: t('hotelDetails.error.invalidSearchId.title'),
        });
        return;
      }

      if (error?.indexOf('travel arranger is allowed') !== -1) {
        toast.add({
          type: 'danger',
          message: t('hotelDetails.error.userDoesNotBelongToTheTravelArranger'),
        });
        return;
      }

      if (error?.indexOf('Debes pertenecer a un grupo') !== -1) {
        toast.add({
          type: 'danger',
          message: t('hotelDetails.error.userMustBeAssignedToGroup'),
        });
        return;
      }

      toast.add({
        type: 'danger',
        message: message || t('hotelDetails.error.generic'),
      });

      Sentry.captureMessage('[HotelDetails] Could not create travel');
    }
  };

  const setConfirmFlow = (message: ConfirmMessage) => {
    setShowConfirmFlow(true);
    setShowSuccess(false);
    setConfirmFlowMessage(message);
  };

  const handleConfirmFlow = async () => {
    if (confirmFlowType === 'invalidOptionId') {
      handleInvalidOptionIdFlow();
    }

    if (confirmFlowType === 'invalidSearchId') {
      setData(undefined);
      setIsLoading(false);
      setIsCreating(false);
      setShowTravelJustify(false);
      setShowConfirmProfile(false);
      handleInvalidSearchIdFlow();
    }
  };

  const handleInvalidOptionIdFlow = async () => {
    handleShowTravelJustify(false);
    handleShowConfirmProfile(false);
    setShowConfirmFlow(false);
    onClose();

    if (filters !== undefined) {
      onFilterChange(filters);
    } else {
      isFilterLoading(true);
      await dispatch(hotelResults());
      isFilterLoading(false);
    }
    history.replace('/corporate/hotels');
  };

  const handleInvalidSearchIdFlow = async () => {
    if (destinationPoint && checkInDate && checkOutDate) {
      await dispatch(
        hotelsSearchParameters({
          destinationPoint,
          checkInDate,
          checkOutDate,
          travelers,
        }),
      );
    }

    history.replace('/corporate/hotels');
    setShowConfirmFlow(false);
  };

  const handleShowConfirmProfile = (value: boolean) => {
    setShowConfirmProfile(value);
  };

  const handleShowTravelJustify = (value: boolean) => {
    setShowTravelJustify(value);
  };

  const handleConfirmFlowClose = () => {};

  return (
    <HotelDetails
      show={show}
      loading={isLoading}
      creating={isCreating}
      details={data}
      onClose={onClose}
      onConfirm={handleConfirm}
      amenitiesCodes={amenitiesCodes}
      showSuccess={showSuccess}
      travelers={travelers}
      onConfirmFlow={handleConfirmFlow}
      onConfirmFlowClose={handleConfirmFlowClose}
      confirmFlowMessage={confirmFlowMessage}
      showConfirmFlow={showConfirmFlow}
      showTravelJustify={showTravelJustify}
      showConfirmProfile={showConfirmProfile}
      setShowConfirmProfile={handleShowConfirmProfile}
      setShowTravelJustify={handleShowTravelJustify}
    />
  );
};

export default HotelDetailsContainer;
